import React from 'react';

import i18n from 'util/i18n';

import { LEGAL, LEGAL_POLICE_OFFENSES } from 'config/formSections/legal';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString, getYearsString } from 'helpers/text';

import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import BranchCollection from 'components/Form/BranchCollection';
import Branch from 'components/Form/Branch';

import { formIncludesPvqPartB, isPVQForm } from 'helpers/form';
import { LegalPoliceOffenseModel } from 'forms/sections/legal/police/offense/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { CommonOnError, CommonOnUpdate } from 'types/props';
import { EmptyBranchCollection } from 'forms/shared/validators/models/branchCollection/instance';
import Offense from './Offense';

const sectionConfig = {
  key: LEGAL_POLICE_OFFENSES.key,
  section: LEGAL.name,
  store: LEGAL.store,
  subsection: LEGAL_POLICE_OFFENSES.name,
  storeKey: LEGAL_POLICE_OFFENSES.storeKey,
};

interface Props extends Partial<LegalPoliceOffenseModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  formType?: string
  requireLegalOffenseInvolvements?: boolean
  requireLegalOffenseSentenced?: boolean
  requireLegalOffenseIncarcerated?: boolean
  errors?: any
}

export class Offenses extends Subsection<Props> {
  static defaultProps = {
    onUpdate: () => { },
    onError: (value, arr) => arr,
    addressBooks: {},
    dispatch: () => { },
    defaultState: true,
    scrollToBottom: '',
    errors: [],
    HasFelonyMoreThanXYears: {},
    HasChargesInvolvingFirearmsMoreThanXYears: {},
    HasDomesticViolenceMoreThanXYears: {},
    HasCitationLastXYears: {},
    HasChargesLastXYears: {},
    HasProbationLastXYears: {},
    HasParoleLastXYears: {},
  };

  constructor(props: Props) {
    super(props);
    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);
  }

  update(queue: Partial<LegalPoliceOffenseModel>) {
    const listUpdate = this.getListDetails();
    this.props.onUpdate?.(this.storeKey, {
      List: listUpdate,
      HasFelonyMoreThanXYears: this.props.HasFelonyMoreThanXYears,
      HasChargesInvolvingFirearmsMoreThanXYears: this.props.HasChargesInvolvingFirearmsMoreThanXYears,
      HasDomesticViolenceMoreThanXYears: this.props.HasDomesticViolenceMoreThanXYears,
      HasCitationLastXYears: this.props.HasCitationLastXYears,
      HasChargesLastXYears: this.props.HasChargesLastXYears,
      HasProbationLastXYears: this.props.HasProbationLastXYears,
      HasParoleLastXYears: this.props.HasParoleLastXYears,
      ...queue,
    });
  }

  updateList = (collection: LegalPoliceOffenseModel['List']) => {
    this.update({ List: collection });
  };

  getListDetails() {
    const isPVQ = isPVQForm(this.props.formType);
    const isPVQB = formIncludesPvqPartB(this.props.formType);

    if (isPVQ) {
      if (isPVQB) {
        if (
          this.props.HasFelonyMoreThanXYears?.value === 'Yes' ||
          this.props.HasChargesInvolvingFirearmsMoreThanXYears?.value === 'Yes' ||
          this.props.HasDomesticViolenceMoreThanXYears?.value === 'Yes'
        ) {
          return { List: this.props.List }
        }
      }

      // PVQ A
      if (
        this.props.HasCitationLastXYears?.value === 'Yes' ||
        this.props.HasChargesLastXYears?.value === 'Yes' ||
        this.props.HasProbationLastXYears?.value === 'Yes' ||
        this.props.HasParoleLastXYears?.value === 'Yes'
      ) {
        return { List: this.props.List }
      }
    } else {
      // SF
      return { List: this.props.List }
    }

    return { List: EmptyBranchCollection() }
  }

  updateHasFelonyMoreThanXYears = (value: LegalPoliceOffenseModel['HasFelonyMoreThanXYears']) => {
    this.update({ HasFelonyMoreThanXYears: value });
  };

  updateHasChargesInvolvingFirearmsMoreThanXYears = (value: LegalPoliceOffenseModel['HasChargesInvolvingFirearmsMoreThanXYears']) => {
    this.update({ HasChargesInvolvingFirearmsMoreThanXYears: value });
  };

  updateHasChargesLastXYears = (value: LegalPoliceOffenseModel['HasChargesLastXYears']) => {
    this.update({ HasChargesLastXYears: value });
  };

  updateHasCitationLastXYears = (value: LegalPoliceOffenseModel['HasCitationLastXYears']) => {
    this.update({ HasCitationLastXYears: value });
  };

  updateHasDomesticViolenceMoreThanXYears = (value: LegalPoliceOffenseModel['HasDomesticViolenceMoreThanXYears']) => {
    this.update({ HasDomesticViolenceMoreThanXYears: value });
  };

  updateHasProbationLastXYears = (value: LegalPoliceOffenseModel['HasProbationLastXYears']) => {
    this.update({ HasProbationLastXYears: value });
  };

  updateHasParoleLastXYears = (value: LegalPoliceOffenseModel['HasParoleLastXYears']) => {
    this.update({ HasParoleLastXYears: value });
  };

  render() {
    const {
      formType,
      requireLegalOffenseInvolvements,
      requireLegalOffenseSentenced,
      requireLegalOffenseIncarcerated,
      errors,
    } = this.props;

    const formTypeConfig = (formType && formConfig[formType]) || undefined;
    const years = Number(formTypeConfig?.LEGAL_POLICE_RECORD_YEARS || 7);
    const numberOfYearsString: string = getNumberOfYearsString(years);
    const yearsString: string = getYearsString(years);

    const collectionErrors = errors && errors.filter((e) => e.indexOf('List.branchCollection') === 0);

    const isPvq = isPVQForm(formType);
    const isPvqB = formIncludesPvqPartB(formType);

    const hasOffences = (
      this.props.HasChargesLastXYears?.value === 'Yes'
      || this.props.HasCitationLastXYears?.value === 'Yes'
      || this.props.HasParoleLastXYears?.value === 'Yes'
      || this.props.HasProbationLastXYears?.value === 'Yes'
      || (isPvqB && (
        this.props.HasChargesInvolvingFirearmsMoreThanXYears?.value === 'Yes'
        || this.props.HasDomesticViolenceMoreThanXYears?.value === 'Yes'
        || this.props.HasFelonyMoreThanXYears?.value === 'Yes'
      ))
    );
    return isPvq ? (
      <div
        className="section-content police-offenses"
        data-section={LEGAL.key}
        data-subsection={LEGAL_POLICE_OFFENSES.key}
      >
        <h1 className="section-header">{i18n.t('legal.subsection.police.offenses')}</h1>
        <p>
          {i18n.m('legal.pvq.police.para.offenses1')}
        </p>
        <p>
          {i18n.m('legal.pvq.police.para.offenses2')}
        </p>
        <Branch
          label={i18n.m(`legal.pvq.police.label.summons`, { numberOfYearsString })}
          labelSize="h4"
          name="HasCitationLastXYears"
          {...this.props.HasCitationLastXYears}
          attribute="HasCitationLastXYears"
          className="HasCitation-LastXYears"
          onUpdate={this.updateHasCitationLastXYears}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError}
        />
        <p>
          {i18n.m('legal.pvq.police.para.offenses3')}
        </p>
        <Branch
          label={i18n.t(`legal.pvq.police.label.charges`, { numberOfYearsString })}
          labelSize="h4"
          name="HasChargesLastXYears"
          {...this.props.HasChargesLastXYears}
          attribute="HasChargesLastXYears"
          className="HasCharges-LastXYears"
          onUpdate={this.updateHasChargesLastXYears}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError}
        />
        <Branch
          label={i18n.t(`legal.pvq.police.label.probation`, { numberOfYearsString })}
          labelSize="h4"
          name="HasProbationLastXYears"
          {...this.props.HasProbationLastXYears}
          attribute="HasProbationLastXYears"
          className="HasProbation-LastXYears"
          onUpdate={this.updateHasProbationLastXYears}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError}
        />
        <Branch
          label={i18n.t(`legal.pvq.police.label.parole`, { numberOfYearsString })}
          labelSize="h4"
          name="HasParoleLastXYears"
          {...this.props.HasParoleLastXYears}
          attribute="HasParoleLastXYears"
          className="HasParole-LastXYears"
          onUpdate={this.updateHasParoleLastXYears}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError}
        />
        {isPvqB && (
          <>
            <Branch
              label={i18n.t(`legal.pvq.police.heading.policeRecordCharged`, { numberOfYearsString })}
              labelSize="h4"
              name="HasFelonyMoreThanXYears"
              {...this.props.HasFelonyMoreThanXYears}
              attribute="HasFelonyMoreThanXYears"
              className="HasFelony-MoreThanXYears"
              onUpdate={this.updateHasFelonyMoreThanXYears}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
            />
            <Branch
              label={i18n.t(`legal.pvq.police.heading.policeRecordFirearms`, { numberOfYearsString })}
              labelSize="h4"
              name="HasChargesInvolvingFirearmsMoreThanXYears"
              {...this.props.HasChargesInvolvingFirearmsMoreThanXYears}
              attribute="HasChargesInvolvingFirearmsMoreThanXYears"
              className="HasChargesInvolvingFirearms-MoreThanXYears"
              onUpdate={this.updateHasChargesInvolvingFirearmsMoreThanXYears}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
            />
            <Branch
              label={i18n.t(`legal.pvq.police.heading.policeRecordDomesticViolence`, { numberOfYearsString })}
              labelSize="h4"
              name="HasDomesticViolenceMoreThanXYears"
              {...this.props.HasDomesticViolenceMoreThanXYears}
              attribute="HasDomesticViolenceMoreThanXYears"
              className="HasDomesticViolence-MoreThanXYears"
              onUpdate={this.updateHasDomesticViolenceMoreThanXYears}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              onError={this.props.onError}
            />
          </>
        )}

        {hasOffences && (
          <BranchCollection
            {...this.props.List}
            branchName="has_offenses"
            className="has-offenses"
            warning
            appendSize="h4"
            // Do you have another offense to report in the last [...]
            appendLabel={i18n.m('legal.pvq.police.collection.appendTitle', { years, yearsString })}
            onUpdate={this.updateList}
            scrollToBottom={this.props.scrollToBottom}
            required={this.props.required}
            onError={this.handleError}
            scrollIntoView={this.props.scrollIntoView}
            hideInitialBranchQuestionAssumeYes
            errors={collectionErrors}
            attribute="List"
          >
            <Offense
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              defaultState={this.props.defaultState}
              onError={this.handleError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={formType}
              requireLegalOffenseInvolvements={requireLegalOffenseInvolvements}
              requireLegalOffenseSentenced={requireLegalOffenseSentenced}
              requireLegalOffenseIncarcerated={requireLegalOffenseIncarcerated}
              attribute="List"
            />
          </BranchCollection>
        )}
      </div>
    ) : (
      <div
        className="section-content police-offenses"
        data-section={LEGAL.key}
        data-subsection={LEGAL_POLICE_OFFENSES.key}
      >
        <h1 className="section-header">{i18n.t('legal.subsection.police.offenses')}</h1>

        <BranchCollection
          {...this.props.List}
          branchName="has_offenses"
          label={i18n.t('legal.police.heading.questions')}
          labelSize="h4"
          className="has-offenses"
          warning
          appendLabel={i18n.t('legal.police.collection.appendTitle')}
          appendSize="h4"
          appendContent={i18n.m('legal.police.collection.appendMessage', { numberOfYearsString })}
          onUpdate={this.updateList}
          scrollToBottom={this.props.scrollToBottom}
          required={this.props.required}
          onError={this.handleError}
          scrollIntoView={this.props.scrollIntoView}
          content={(
            <ul>
              <li>{i18n.m(`legal.police.label.summons`, { numberOfYearsString })}</li>
              <li>{i18n.m(`legal.police.label.arrests`, { numberOfYearsString })}</li>
              <li>{i18n.m(`legal.police.label.charges`, { numberOfYearsString })}</li>
              <li>{i18n.m(`legal.police.label.probation`, { numberOfYearsString })}</li>
              <li>{i18n.m(`legal.police.label.trial`)}</li>
            </ul>
          )}
          errors={collectionErrors}
          attribute="List"
        >
          <Offense
            name="Item"
            bind
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            defaultState={this.props.defaultState}
            onError={this.handleError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireLegalOffenseInvolvements={requireLegalOffenseInvolvements}
            requireLegalOffenseSentenced={requireLegalOffenseSentenced}
            requireLegalOffenseIncarcerated={requireLegalOffenseIncarcerated}
            attribute="List"
          />
        </BranchCollection>
      </div>
    );
  }
}

export default connectSubsection(Offenses, sectionConfig);
